import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../base';
import { ItemCreatedResponse, GenericItemResponse, GenericCollectionResponse } from '../../entities';
import { Referral } from './entities';
import { ReferralMessage } from './entities';
import { CreateReferralRequest } from './entities';
import { AcceptReferralRequest } from './entities';
import { AcceptReferralResult, AcceptReferralResponse } from './entities';
import { AddReferralMessageRequest } from './entities';
import { UpdateReferralMessageRequest } from './entities';
import { ReferralItemResponse } from './entities';
import { ReferralMessageItemResponse } from './entities';
import { ReferralMessageCollectionResponse } from './entities';
import { ReferralCollectionResponse } from './entities';
import { PracticeReferralPatientSnapshot } from './entities';
import { CreateReferralSnapshotRequest } from './entities';

@Injectable({
  providedIn: 'root'
})
export class ReferralsRepositoryService {
  constructor(private api: ApiService) {}

  getById(id: number): Observable<Referral> {
    return this.api.get<ReferralItemResponse>('/referrals/' + id)
      .pipe(map(response => response.value));
  }

  getByIdWithPatient(id: number): Observable<Referral> {
    return this.api.get<ReferralItemResponse>('/referrals/' + id)
      .pipe(map(response => response.value));
  }

  getOutgoing(): Observable<Referral[]> {
    return this.api.get<ReferralCollectionResponse>('/referrals/outgoing')
      .pipe(map(response => response.items));
  }

  getIncoming(): Observable<Referral[]> {
    return this.api.get<ReferralCollectionResponse>('/referrals/incoming')
      .pipe(map(response => response.items));
  }

  create(request: CreateReferralRequest): Observable<number> {
    return this.api.post<ItemCreatedResponse>('/referrals', request)
      .pipe(map(response => response.value));
  }

  accept(id: number, request: AcceptReferralRequest): Observable<AcceptReferralResult> {
    return this.api.post<AcceptReferralResponse>('/referrals/' + id + '/accept', request)
      .pipe(map(response => response.value));
  }

  reject(id: number): Observable<boolean> {
    return this.api.post<GenericItemResponse<boolean>>('/referrals/' + id + '/reject', {})
      .pipe(map(response => response.value));
  }

  addMessage(id: number, request: AddReferralMessageRequest): Observable<ReferralMessage> {
    return this.api.post<ReferralMessageItemResponse>('/referrals/' + id + '/messages', request)
      .pipe(map(response => response.value));
  }

  updateMessage(id: number, messageId: number, request: UpdateReferralMessageRequest): Observable<ReferralMessage> {
    return this.api.put<ReferralMessageItemResponse>('/referrals/' + id + '/messages/' + messageId, request)
      .pipe(map(response => response.value));
  }

  getMessages(id: number): Observable<ReferralMessage[]> {
    return this.api.get<ReferralMessageCollectionResponse>('/referrals/' + id + '/messages')
      .pipe(map(response => response.items));
  }

  getSnapshots(referralId: number): Observable<PracticeReferralPatientSnapshot[]> {
    return this.api.get<GenericCollectionResponse<PracticeReferralPatientSnapshot>>('/referrals/' + referralId + '/snapshots')
      .pipe(map(response => response.items));
  }

  getSnapshot(referralId: number, snapshotId: number): Observable<PracticeReferralPatientSnapshot> {
    return this.api.get<GenericItemResponse<PracticeReferralPatientSnapshot>>('/referrals/' + referralId + '/snapshots/' + snapshotId)
      .pipe(map(response => response.value));
  }

  createSnapshot(referralId: number, request: CreateReferralSnapshotRequest): Observable<number> {
    return this.api.post<ItemCreatedResponse>('/referrals/' + referralId + '/snapshots', request)
      .pipe(map(response => response.value));
  }

  importSnapshot(referralId: number, snapshotId: number, request: AcceptReferralRequest): Observable<boolean> {
    return this.api.post<GenericItemResponse<boolean>>('/referrals/' + referralId + '/snapshots/' + snapshotId + '/import', request)
      .pipe(map(response => response.value));
  }
}
